import { Link } from '@tanstack/react-router';
import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';
import { session } from '@/shared/session/session';

import { useOrganizationSettings } from '../hooks/use-organization-settings';
import { useUpdateOrganizationSettings } from '../hooks/use-update-organization-settings';
import { securityRefusal } from '../schemas/organization-settings-rules';

/**
 * "Require two-factor for all staff". Owners only; turning it on needs the owner's own
 * two-factor first, or the backend refuses (it would lock the owner out of this very switch).
 */
export function RequireTwoFactorCard() {
  const { t } = useTranslation('organization');
  const message = useErrorMessage();
  const { me } = session.useSession();
  const query = useOrganizationSettings();
  const update = useUpdateOrganizationSettings();
  const isOwner = me?.role.key === 'owner';
  const required = query.data?.security.requireTwoFactor ?? false;
  const refusal = update.isError ? securityRefusal(update.error) : null;

  return (
    <Card
      id={SECTION_ANCHORS.requireTwoFactor}
      className="max-w-2xl"
      data-tour="org.settings.require-2fa"
    >
      <CardHeader>
        <CardTitle>{t('security.title')}</CardTitle>
        <CardDescription>{t('security.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />
        ) : query.isPending ? (
          <Skeleton className="h-10" />
        ) : (
          <div className="flex items-start gap-3">
            <Switch
              id="require-two-factor"
              checked={required}
              disabled={!isOwner || update.isPending}
              onCheckedChange={(next) => {
                update.reset();
                update.mutate({ security: { requireTwoFactor: next } });
              }}
              className="mt-0.5"
            />
            <div className="grid gap-1">
              <Label htmlFor="require-two-factor">{t('security.requireTwoFactor')}</Label>
              <p className="text-muted-foreground text-sm">
                {required ? t('security.onHint') : t('security.offHint')}
              </p>
            </div>
          </div>
        )}
        {!isOwner ? (
          <p className="text-muted-foreground text-sm" data-testid="owners-only">
            {t('security.ownersOnly')}
          </p>
        ) : null}
        {me && isOwner && !me.twoFactorEnabled && !required ? (
          <Alert>
            <ShieldAlert aria-hidden />
            <AlertDescription>
              {t('security.enrolFirst')}{' '}
              <Link to="/account/security" className="font-medium underline">
                {t('security.openSecurity')}
              </Link>
            </AlertDescription>
          </Alert>
        ) : null}
        {refusal === 'owner_needs_two_factor' ? (
          <Alert variant="destructive" role="alert">
            <ShieldAlert aria-hidden />
            <AlertTitle>{t('security.needsTwoFactorTitle')}</AlertTitle>
            <AlertDescription>
              {t('security.needsTwoFactor')}{' '}
              <Link to="/account/security" className="font-medium underline">
                {t('security.openSecurity')}
              </Link>
            </AlertDescription>
          </Alert>
        ) : refusal === 'not_owner' ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{t('security.notOwner')}</AlertDescription>
          </Alert>
        ) : update.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{message(update.error)}</AlertDescription>
          </Alert>
        ) : null}
        {required ? (
          <p className="text-success flex items-center gap-1.5 text-sm">
            <ShieldCheck className="size-4" aria-hidden />
            {t('security.enforced')}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
